// src/components/PricingPlans.js
import React, { useState } from 'react';
import { Card, Row, Col, Button, Radio, Typography, List, Tag, Badge } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const PricingPlans = ({ 
    plans, 
    onSelect, 
    showSelectButton = true, // Default to true
    currentPlanId = null,    // ID of the currently active plan
    selectedPlanId = null   // ID of the plan currently clicked/selected by user
}) => {
    const [billingInterval, setBillingInterval] = useState('monthly');

    const handleSelect = (plan) => {
        if (onSelect) { // Only call onSelect if provided
            onSelect({
                ...plan,
                selectedInterval: billingInterval
            });
        }
    };

    const getPrice = (plan) => {
        return billingInterval === 'monthly' ? plan.price.monthly : plan.price.yearly;
    };

    const getPeriodText = () => {
        return billingInterval === 'monthly' ? '/Monat' : '/Jahr';
    };

    const getSavings = (plan) => {
        if (billingInterval === 'yearly') {
            const monthlyTotal = plan.price.monthly * 12;
            const yearlyPrice = plan.price.yearly;
            if (monthlyTotal > yearlyPrice) {
                const savingsPercentage = ((monthlyTotal - yearlyPrice) / monthlyTotal * 100).toFixed(0);
                return `${savingsPercentage}% sparen`;
            }
        }
        return null;
    };

    return (
        <div style={{ width: '100%' }}>
            <Row justify="center" style={{ marginBottom: '3rem' }}>
                <Col>
                    <Radio.Group
                        value={billingInterval}
                        onChange={(e) => setBillingInterval(e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                    >
                        <Radio.Button value="monthly">Monatlich</Radio.Button>
                        <Radio.Button value="yearly">Jährlich</Radio.Button>
                    </Radio.Group>
                    {billingInterval === 'yearly' && (
                        <Tag color="success" style={{ marginLeft: '10px', verticalAlign: 'middle' }}>2 Monate gratis!</Tag>
                    )}
                </Col>
            </Row>

            <Row gutter={[24, 24]} justify="center">
                {plans.map(plan => {
                    const isCurrent = plan.id === currentPlanId;
                    const isSelected = plan.id === selectedPlanId;

                    let cardWrapperStyle = {}; // Style for the outer Col or Badge
                    let cardStyle = {
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: plan.recommended ? 'var(--box-shadow)' : 'none',
                        border: '1px solid #f0f0f0',
                        transition: 'border-color 0.3s ease, box-shadow 0.3s ease' // Add transition
                    };

                    let headStyle = {};

                    if (isCurrent) {
                        cardStyle.borderColor = 'var(--ant-primary-color, #1890ff)';
                        cardStyle.borderWidth = '2px';
                        headStyle.backgroundColor = '#e6f7ff'; // Highlight header for current plan
                    }
                    if (isSelected) {
                        // Ensure selected style overrides others for border
                        cardStyle.borderColor = 'var(--ant-success-color, #52c41a)'; 
                        cardStyle.borderWidth = '2px';
                        cardStyle.boxShadow = '0 0 10px rgba(82, 196, 26, 0.3)'; // Add subtle glow for selected
                        headStyle = isCurrent ? { ...headStyle, borderBottom: '1px solid var(--ant-primary-color, #1890ff)' } : {}; // Keep current header style if also selected
                    }

                    const PlanCard = (
                        <Card
                            title={plan.name}
                            bordered={false}
                            style={cardStyle}
                            bodyStyle={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
                            headStyle={headStyle}
                        >
                             {isCurrent && (
                                <Tag color="blue" style={{ position: 'absolute', top: '16px', right: '16px' }}>Aktuell</Tag>
                             )}
                             {/* Keep recommended ribbon separate */}
                            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                <Title level={3} style={{ fontSize: '2.5rem', margin: 0 }}>
                                    {getPrice(plan).toFixed(2)}€
                                </Title>
                                <Text type="secondary">{getPeriodText()}</Text>
                                {getSavings(plan) && (
                                    <Paragraph style={{ color: 'green', marginTop: '5px', marginBottom: '0' }}>
                                        {getSavings(plan)}
                                    </Paragraph>
                                )}
                            </div>
                            <List
                                dataSource={plan.features}
                                renderItem={item => (
                                    <List.Item style={{ borderBottom: 'none', padding: '5px 0' }}>
                                        <CheckCircleOutlined style={{ color: 'var(--success-color, green)', marginRight: '8px' }} /> {item}
                                    </List.Item>
                                )}
                                style={{ marginBottom: 'auto', flexGrow: 1 }}
                            />
                            {showSelectButton && (
                                <Button
                                    type={plan.recommended || isSelected ? "primary" : "default"}
                                    block
                                    onClick={() => handleSelect(plan)}
                                    style={{ marginTop: '20px' }}
                                    size="large"
                                    disabled={isCurrent} // Disable button for the current plan
                                >
                                    {isCurrent ? 'Aktueller Plan' : (isSelected ? 'Ausgewählt' : 'Plan auswählen')}
                                </Button>
                            )}
                        </Card>
                    );

                    return (
                        // Apply wrapper style if needed, e.g., for spacing or effects
                        <Col key={plan.id} xs={24} sm={12} md={8} style={cardWrapperStyle}>
                            {plan.recommended && !isCurrent ? (
                                <Badge.Ribbon text="Empfohlen" color="blue">
                                    {PlanCard}
                                </Badge.Ribbon>
                            ) : (
                                PlanCard
                            )}
                        </Col>
                    );
                })}
            </Row>
        </div>
    );
};

export default PricingPlans;